import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  Typography,
  MenuItem,
  CircularProgress,
  Avatar,
  IconButton,
  Tabs,
  Tab,
  Divider,
  Radio,
  RadioGroup,
  FormControlLabel,
  Chip,
  Checkbox,
  Paper,
} from '@mui/material';
import {
  Person,
  Business,
  Add,
  Star,
  StarBorder,
  Lightbulb,
  AttachMoney,
  Receipt,
  CalendarMonth,
  ConfirmationNumber,
  TaskAlt,
  ShoppingCart,
} from '@mui/icons-material';
import { customerAPI, paymentTermAPI, quotationAPI } from '../../services/api';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import { ViewPageHeader, StatButton, ActivityPanel, SalespersonSelect } from '../../components/common';

const countryOptions = ['India', 'United Kingdom (UK)'];

// Validation helpers
const validateEmail = (email) => {
  if (!email) return true; // Optional field
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return emailRegex.test(email);
};

const validatePhone = (phone) => {
  if (!phone) return true; // Optional field
  const phoneRegex = /^[+]?[0-9]{10,15}$/;
  return phoneRegex.test(phone.replace(/[\s-]/g, ''));
};

const CustomerForm = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [activityTab, setActivityTab] = useState(0);
  const [paymentTermsList, setPaymentTermsList] = useState([]);
  const [validationErrors, setValidationErrors] = useState({});
  const [cityOptions, setCityOptions] = useState(['Gurugram', 'Mumbai', 'Bangalore']);

  const [formData, setFormData] = useState({
    type: 'person', // 'person' or 'company'
    name: '',
    email: '',
    phone: '',
    company: '',
    // Address
    street: '',
    city: '',
    zip: '',
    state: '',
    country: 'India',
    // Tax Info
    gstin: '',
    // Internal
    internalNotes: '',
    status: 'lead',
    // Sales & Purchase - SALES section
    salesperson: '',
    paymentTerms: '',
    paymentMethod: '',
    pricelist: 'default_inr',
    deliveryMethod: '',
    // Sales & Purchase - FISCAL INFORMATION
    fiscalPosition: '',
    // Sales & Purchase - PURCHASE section
    buyer: '',
    purchasePaymentTerms: '',
    purchasePaymentMethod: '',
    receiptReminder: false,
    supplierCurrency: '',
    // Sales & Purchase - MISC section
    reference: '',
    companyVisibility: 'visible_to_all',
    slaPolicies: '',
    // Invoicing - GENERAL section
    banks: '',
    // Invoicing - CUSTOMER INVOICES section
    invoiceSending: 'e_invoicing_email',
    eInvoiceFormat: 'xml_format',
    peppolId: '',
    // Invoicing - INVOICE FOLLOW-UPS section
    followUpStatus: 'no_action_needed',
    reminders: 'automatic',
    nextReminder: '',
    responsible: '',
  });

  useEffect(() => {
    fetchPaymentTerms();
    fetchCityOptions();
  }, []);

  const fetchPaymentTerms = async () => {
    try {
      const response = await paymentTermAPI.getAllForDropdown();
      setPaymentTermsList(response.data);
    } catch (error) {
      console.error('Error fetching payment terms:', error);
    }
  };

  const fetchCityOptions = async () => {
    try {
      const response = await quotationAPI.getLocations();
      const list = response.data?.locations || [];
      if (list.length > 0) setCityOptions(list);
    } catch (error) {
      console.error('Error fetching city options:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear validation error when user types
    if (validationErrors[name]) {
      setValidationErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async () => {
    // Validation
    const errors = {};
    
    if (!formData.name.trim()) {
      errors.name = 'Name is required';
    }
    
    if (formData.email && !validateEmail(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }
    
    if (formData.phone && !validatePhone(formData.phone)) {
      errors.phone = 'Phone number must be 10-15 digits';
    }
    
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      toast.error(Object.values(errors)[0]);
      return;
    }

    try {
      setSaving(true);
      const payload = {
        type: formData.type,
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        company: formData.type === 'person' ? formData.company : formData.name,
        jobTitle: formData.jobPosition,
        address: {
          street: formData.street,
          city: formData.city,
          zipCode: formData.zip,
          state: formData.state,
          country: formData.country,
        },
        gstTreatment: formData.gstTreatment,
        gstNumber: formData.gstin,
        panNumber: formData.pan,
        website: formData.website,
        tags: formData.tags,
        notes: formData.internalNotes,
        status: formData.status,
        assignedSalesPerson: formData.salesperson || undefined,
      };

      const response = await customerAPI.create(payload);
      toast.success('Contact created successfully');
      navigate(`/customers/${response.data.customer._id}`);
    } catch (error) {
      const data = error.response?.data;
      const message =
        data?.message ||
        data?.errors?.[0]?.msg ||
        'Failed to save contact';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  // Stats buttons for header
  const statsButtons = (
    <>
      <StatButton
        icon={<Lightbulb sx={{ fontSize: 18, color: '#f5c518' }} />}
        title="Opportunities"
        value="0"
      />
      <StatButton
        icon={<AttachMoney sx={{ fontSize: 18, color: '#28a745' }} />}
        title="Sales"
        value="0"
      />
      <StatButton
        icon={<Receipt sx={{ fontSize: 18, color: '#875A7B' }} />}
        title="Invoiced"
        value="₹ 0.00"
      />
      <StatButton
        icon={<CalendarMonth sx={{ fontSize: 18, color: '#17a2b8' }} />}
        title="Meetings"
        value="0"
      />
      <StatButton
        icon={<ConfirmationNumber sx={{ fontSize: 18, color: '#dc3545' }} />}
        title="Tickets"
        value="0"
      />
      <StatButton
        icon={<TaskAlt sx={{ fontSize: 18, color: '#6f42c1' }} />}
        title="Tasks"
        value="0"
      />
      <StatButton
        icon={<ShoppingCart sx={{ fontSize: 18, color: '#fd7e14' }} />}
        title="Purchases"
        value="0"
        isLast
      />
    </>
  );

  return (
    <Box sx={{ backgroundColor: '#f5f5f5', minHeight: 'calc(100vh - 64px)' }}>
      {/* Header */}
      <ViewPageHeader
        parentTitle="Customers"
        currentTitle="New"
        onParentClick={() => navigate('/customers')}
        onNewClick={() => navigate('/customers/new')}
        onClose={() => navigate('/customers')}
        statsButtons={statsButtons}
      />

      {/* Main Content */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, p: 2, gap: 2 }}>
        {/* Left - Form */}
        <Box sx={{ flex: 1 }}>
          <Paper sx={{ p: 3, border: '1px solid #dee2e6', boxShadow: 'none' }}>
            {/* Top Section - Avatar and Basic Info */}
            <Box sx={{ display: 'flex', gap: 3, mb: 3 }}>
              {/* Avatar Section */}
              <Avatar
                sx={{
                  width: 110,
                  height: 110,
                  bgcolor: formData.name ? '#5c6bc0' : '#f8f9fa',
                  fontSize: '48px',
                  fontWeight: 400,
                  borderRadius: '4px',
                  border: '1px solid #dee2e6',
                  cursor: 'pointer',
                }}
              >
                {formData.name ? formData.name.charAt(0).toUpperCase() : (
                  formData.type === 'person' ? 
                    <Person sx={{ fontSize: 60, color: '#adb5bd' }} /> : 
                    <Business sx={{ fontSize: 60, color: '#adb5bd' }} />
                )}
              </Avatar>

              {/* Right Side - Type Toggle and Fields */}
              <Box sx={{ flex: 1 }}>
                {/* Person/Company Toggle */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <RadioGroup
                    row
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                  >
                    <FormControlLabel
                      value="person"
                      control={
                        <Radio
                          size="small"
                          sx={{
                            color: formData.type === 'person' ? '#00A09D' : '#6c757d',
                            '&.Mui-checked': { color: '#00A09D' },
                          }}
                        />
                      }
                      label={<Typography sx={{ fontSize: '14px', color: '#333' }}>Person</Typography>}
                    />
                    <FormControlLabel
                      value="company"
                      control={
                        <Radio
                          size="small"
                          sx={{
                            color: formData.type === 'company' ? '#00A09D' : '#6c757d',
                            '&.Mui-checked': { color: '#00A09D' },
                          }}
                        />
                      }
                      label={<Typography sx={{ fontSize: '14px', color: '#333' }}>Company</Typography>}
                    />
                  </RadioGroup>
                </Box>

                {/* Name Field - Large */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <IconButton size="small" sx={{ p: 0 }}>
                    <StarBorder sx={{ fontSize: 24, color: '#999' }} />
                  </IconButton>
                  <TextField
                    fullWidth
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder={formData.type === 'person' ? 'e.g. Brandon Freeman' : 'e.g. Lumber Inc'}
                    variant="standard"
                    InputProps={{
                      disableUnderline: true,
                      sx: {
                        fontSize: '28px',
                        fontWeight: 400,
                        color: '#333',
                      },
                    }}
                  />
                </Box>

                {/* Email and Phone */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <Typography sx={{ fontSize: '14px', color: '#666' }}>✉</Typography>
                  <TextField
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="email@example.com"
                    variant="standard"
                    size="small"
                    InputProps={{
                      disableUnderline: true,
                      sx: { fontSize: '14px', color: '#017e84' },
                    }}
                    sx={{ flex: 1 }}
                  />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography sx={{ fontSize: '14px', color: '#666' }}>📞</Typography>
                  <TextField
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+1 234-567-8900"
                    variant="standard"
                    size="small"
                    InputProps={{
                      disableUnderline: true,
                      sx: { fontSize: '14px', color: '#333' },
                    }}
                    sx={{ flex: 1 }}
                  />
                </Box>
              </Box>
            </Box>

            {/* Two Column Layout for Details */}
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4, mb: 3 }}>
              {/* Left Column */}
              <Box sx={{ flex: 1 }}>
                {/* Company (only for Person type) */}
                {formData.type === 'person' && (
                  <Box sx={{ display: 'flex', mb: 2 }}>
                    <Typography sx={{ width: 100, color: '#495057', fontSize: '13px' }}>Company</Typography>
                    <TextField
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      placeholder="Company Name..."
                      variant="standard"
                      size="small"
                      fullWidth
                      InputProps={{
                        disableUnderline: true,
                        sx: { fontSize: '13px' },
                      }}
                      sx={{ borderBottom: '1px solid #dee2e6' }}
                    />
                  </Box>
                )}

                {/* Address Section */}
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                    <Typography sx={{ width: 100, color: '#495057', fontSize: '13px', pt: 0.5 }}>Address</Typography>
                    <Box sx={{ flex: 1 }}>
                      <TextField
                        name="street"
                        value={formData.street}
                        onChange={handleChange}
                        placeholder="Street..."
                        variant="standard"
                        size="small"
                        fullWidth
                        InputProps={{
                          disableUnderline: true,
                          sx: { fontSize: '13px' },
                        }}
                        sx={{ borderBottom: '1px solid #dee2e6', mb: 1 }}
                      />
                      <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
                        <TextField
                          select
                          name="city"
                          value={formData.city}
                          onChange={handleChange}
                          variant="standard"
                          size="small"
                          InputProps={{
                            disableUnderline: true,
                            sx: { fontSize: '13px' },
                          }}
                          SelectProps={{
                            displayEmpty: true,
                            renderValue: (value) => value || <span style={{ color: '#999' }}>City</span>,
                          }}
                          sx={{ flex: 1, borderBottom: '1px solid #dee2e6' }}
                        >
                          <MenuItem value=""><em style={{ color: '#999' }}>City</em></MenuItem>
                          {cityOptions.map((city) => (
                            <MenuItem key={city} value={city}>
                              {city}
                            </MenuItem>
                          ))}
                        </TextField>
                        <TextField
                          name="zip"
                          value={formData.zip}
                          onChange={handleChange}
                          placeholder="ZIP"
                          variant="standard"
                          size="small"
                          InputProps={{
                            disableUnderline: true,
                            sx: { fontSize: '13px' },
                          }}
                          sx={{ width: 80, borderBottom: '1px solid #dee2e6' }}
                        />
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography sx={{ fontSize: '13px', color: '#495057' }}></Typography>
                        <TextField
                          select
                          name="country"
                          value={formData.country}
                          onChange={handleChange}
                          variant="standard"
                          size="small"
                          InputProps={{
                            disableUnderline: true,
                            sx: { fontSize: '13px' },
                          }}
                          sx={{ width: 150, borderBottom: '1px solid #dee2e6' }}
                        >
                          {countryOptions.map((country) => (
                            <MenuItem key={country} value={country}>
                              {country}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Box>

              {/* Right Column */}
              <Box sx={{ flex: 1 }}>
                {/* GSTIN */}
                <Box sx={{ display: 'flex', mb: 2 }}>
                  <Typography sx={{ width: 120, fontSize: '14px', fontWeight: 500, color: '#333' }}>
                    GSTIN <Typography component="span" sx={{ fontSize: '12px', color: '#999' }}>?</Typography>
                  </Typography>
                  <TextField
                    name="gstin"
                    value={formData.gstin}
                    onChange={handleChange}
                    placeholder="not applicable"
                    variant="standard"
                    size="small"
                    fullWidth
                    InputProps={{
                      disableUnderline: true,
                      sx: { fontSize: '14px' },
                    }}
                  />
                </Box>
              </Box>
            </Box>

            {/* Tabs Section */}
            <Box sx={{ mt: 4 }}>
              <Tabs
                value={activeTab}
                onChange={(e, newValue) => setActiveTab(newValue)}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                sx={{
                  borderBottom: 1,
                  borderColor: 'divider',
                  '& .MuiTab-root': {
                    textTransform: 'none',
                    minWidth: 'auto',
                    px: 2,
                    color: '#666',
                    fontSize: '13px',
                  },
                  '& .Mui-selected': {
                    color: '#212529 !important',
                    fontWeight: 500,
                  },
                  '& .MuiTabs-indicator': {
                    backgroundColor: '#875A7B',
                  },
                }}
              >
                <Tab label="Contacts" />
                <Tab label="Sales & Purchase" />
                <Tab label="Invoicing" />
                <Tab label="Notes" />
              </Tabs>

              {/* Tab Content */}
              <Box sx={{ py: 2 }}>
                {activeTab === 0 && (
                  <Box
                    sx={{
                      width: 200,
                      height: 80,
                      border: '1px dashed #dee2e6',
                      borderRadius: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      '&:hover': { borderColor: '#00A09D' },
                    }}
                  >
                    <Typography sx={{ color: '#00A09D', fontSize: '13px' }}>Add Contact</Typography>
                  </Box>
                )}

                {activeTab === 1 && (
                  <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4 }}>
                    {/* Left Column */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      {/* SALES Section */}
                      <Typography sx={{ fontWeight: 600, color: '#333', fontSize: '13px', mb: 2, pb: 1, borderBottom: '1px solid #e0e0e0' }}>
                        SALES
                      </Typography>
                      <Box sx={{ mb: 3 }}>
                        <SalespersonSelect
                          value={formData.salesperson}
                          onChange={(salespersonId) => setFormData({ ...formData, salesperson: salespersonId })}
                        />
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                          <Typography sx={{ width: 140, color: '#495057', fontSize: '13px' }}>Payment Terms</Typography>
                          <TextField
                            select
                            name="paymentTerms"
                            value={formData.paymentTerms}
                            onChange={handleChange}
                            variant="standard"
                            size="small"
                            fullWidth
                            InputProps={{ disableUnderline: true, sx: { fontSize: '13px' } }}
                            sx={{ borderBottom: '1px solid #dee2e6' }}
                          >
                            <MenuItem value="">Select...</MenuItem>
                            {paymentTermsList.map((term) => (
                              <MenuItem key={term._id} value={term._id}>
                                {term.name}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                          <Typography sx={{ width: 140, color: '#495057', fontSize: '13px' }}>Payment Method</Typography>
                          <TextField
                            select
                            name="paymentMethod"
                            value={formData.paymentMethod}
                            onChange={handleChange}
                            variant="standard"
                            size="small"
                            fullWidth
                            InputProps={{ disableUnderline: true, sx: { fontSize: '13px' } }}
                            sx={{ borderBottom: '1px solid #dee2e6' }}
                          >
                            <MenuItem value="">Select...</MenuItem>
                            <MenuItem value="bank_transfer">Bank Transfer</MenuItem>
                            <MenuItem value="credit_card">Credit Card</MenuItem>
                            <MenuItem value="cash">Cash</MenuItem>
                            <MenuItem value="upi">UPI</MenuItem>
                            <MenuItem value="cheque">Cheque</MenuItem>
                          </TextField>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                          <Typography sx={{ width: 140, color: '#495057', fontSize: '13px' }}>
                            Pricelist <sup style={{ color: '#875A7B', cursor: 'help' }}>?</sup>
                          </Typography>
                          <TextField
                            select
                            name="pricelist"
                            value={formData.pricelist}
                            onChange={handleChange}
                            variant="standard"
                            size="small"
                            fullWidth
                            InputProps={{ disableUnderline: true, sx: { fontSize: '13px' } }}
                            sx={{ borderBottom: '1px solid #dee2e6' }}
                          >
                            <MenuItem value="default_inr">Default (INR)</MenuItem>
                            <MenuItem value="wholesale">Wholesale</MenuItem>
                            <MenuItem value="retail">Retail</MenuItem>
                          </TextField>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography sx={{ width: 140, color: '#495057', fontSize: '13px' }}>
                            Delivery Method <sup style={{ color: '#875A7B', cursor: 'help' }}>?</sup>
                          </Typography>
                          <TextField
                            select
                            name="deliveryMethod"
                            value={formData.deliveryMethod}
                            onChange={handleChange}
                            variant="standard"
                            size="small"
                            fullWidth
                            InputProps={{ disableUnderline: true, sx: { fontSize: '13px' } }}
                            sx={{ borderBottom: '1px solid #dee2e6' }}
                          >
                            <MenuItem value="">Select...</MenuItem>
                            <MenuItem value="pickup">Pickup</MenuItem>
                            <MenuItem value="delivery">Delivery</MenuItem>
                            <MenuItem value="shipping">Shipping</MenuItem>
                          </TextField>
                        </Box>
                      </Box>

                      {/* FISCAL INFORMATION Section */}
                      <Typography sx={{ fontWeight: 600, color: '#333', fontSize: '13px', mb: 2, pb: 1, borderBottom: '1px solid #e0e0e0' }}>
                        FISCAL INFORMATION
                      </Typography>
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography sx={{ width: 140, color: '#495057', fontSize: '13px' }}>
                            Fiscal Position <sup style={{ color: '#875A7B', cursor: 'help' }}>?</sup>
                          </Typography>
                          <TextField
                            name="fiscalPosition"
                            value={formData.fiscalPosition}
                            onChange={handleChange}
                            placeholder="Select..."
                            variant="standard"
                            size="small"
                            fullWidth
                            InputProps={{ disableUnderline: true, sx: { fontSize: '13px' } }}
                            sx={{ borderBottom: '1px solid #dee2e6' }}
                          />
                        </Box>
                      </Box>
                    </Box>

                    {/* Right Column */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      {/* PURCHASE Section */}
                      <Typography sx={{ fontWeight: 600, color: '#333', fontSize: '13px', mb: 2, pb: 1, borderBottom: '1px solid #e0e0e0' }}>
                        PURCHASE
                      </Typography>
                      <Box sx={{ mb: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                          <Typography sx={{ width: 140, color: '#495057', fontSize: '13px' }}>Buyer</Typography>
                          <TextField
                            name="buyer"
                            value={formData.buyer}
                            onChange={handleChange}
                            placeholder="Select..."
                            variant="standard"
                            size="small"
                            fullWidth
                            InputProps={{ disableUnderline: true, sx: { fontSize: '13px' } }}
                            sx={{ borderBottom: '1px solid #dee2e6' }}
                          />
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                          <Typography sx={{ width: 140, color: '#495057', fontSize: '13px' }}>Payment Terms</Typography>
                          <TextField
                            select
                            name="purchasePaymentTerms"
                            value={formData.purchasePaymentTerms}
                            onChange={handleChange}
                            variant="standard"
                            size="small"
                            fullWidth
                            InputProps={{ disableUnderline: true, sx: { fontSize: '13px' } }}
                            sx={{ borderBottom: '1px solid #dee2e6' }}
                          >
                            <MenuItem value="">Select...</MenuItem>
                            {paymentTermsList.map((term) => (
                              <MenuItem key={term._id} value={term._id}>
                                {term.name}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                          <Typography sx={{ width: 140, color: '#495057', fontSize: '13px' }}>Payment Method</Typography>
                          <TextField
                            select
                            name="purchasePaymentMethod"
                            value={formData.purchasePaymentMethod}
                            onChange={handleChange}
                            variant="standard"
                            size="small"
                            fullWidth
                            InputProps={{ disableUnderline: true, sx: { fontSize: '13px' } }}
                            sx={{ borderBottom: '1px solid #dee2e6' }}
                          >
                            <MenuItem value="">Select...</MenuItem>
                            <MenuItem value="bank_transfer">Bank Transfer</MenuItem>
                            <MenuItem value="credit_card">Credit Card</MenuItem>
                            <MenuItem value="cash">Cash</MenuItem>
                          </TextField>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                          <Typography sx={{ width: 140, color: '#495057', fontSize: '13px' }}>
                            Receipt Reminder <sup style={{ color: '#875A7B', cursor: 'help' }}>?</sup>
                          </Typography>
                          <Checkbox
                            checked={formData.receiptReminder}
                            onChange={(e) => setFormData({ ...formData, receiptReminder: e.target.checked })}
                            size="small"
                            sx={{ p: 0, color: '#00A09D', '&.Mui-checked': { color: '#00A09D' } }}
                          />
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography sx={{ width: 140, color: '#495057', fontSize: '13px' }}>
                            Supplier Currency <sup style={{ color: '#875A7B', cursor: 'help' }}>?</sup>
                          </Typography>
                          <TextField
                            select
                            name="supplierCurrency"
                            value={formData.supplierCurrency}
                            onChange={handleChange}
                            variant="standard"
                            size="small"
                            fullWidth
                            InputProps={{ disableUnderline: true, sx: { fontSize: '13px' } }}
                            sx={{ borderBottom: '1px solid #dee2e6' }}
                          >
                            <MenuItem value="">Select...</MenuItem>
                            <MenuItem value="INR">INR</MenuItem>
                            <MenuItem value="USD">USD</MenuItem>
                            <MenuItem value="EUR">EUR</MenuItem>
                            <MenuItem value="GBP">GBP</MenuItem>
                          </TextField>
                        </Box>
                      </Box>

                      {/* MISC Section */}
                      <Typography sx={{ fontWeight: 600, color: '#333', fontSize: '13px', mb: 2, pb: 1, borderBottom: '1px solid #e0e0e0' }}>
                        MISC
                      </Typography>
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                          <Typography sx={{ width: 140, color: '#495057', fontSize: '13px' }}>Reference</Typography>
                          <TextField
                            name="reference"
                            value={formData.reference}
                            onChange={handleChange}
                            placeholder="Enter reference..."
                            variant="standard"
                            size="small"
                            fullWidth
                            InputProps={{ disableUnderline: true, sx: { fontSize: '13px' } }}
                            sx={{ borderBottom: '1px solid #dee2e6' }}
                          />
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                          <Typography sx={{ width: 140, color: '#495057', fontSize: '13px' }}>Company</Typography>
                          <Typography sx={{ fontSize: '13px', color: '#017e84' }}>Visible to all</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography sx={{ width: 140, color: '#495057', fontSize: '13px' }}>
                            SLA Policies <sup style={{ color: '#875A7B', cursor: 'help' }}>?</sup>
                          </Typography>
                          <TextField
                            name="slaPolicies"
                            value={formData.slaPolicies}
                            onChange={handleChange}
                            placeholder="Select..."
                            variant="standard"
                            size="small"
                            fullWidth
                            InputProps={{ disableUnderline: true, sx: { fontSize: '13px' } }}
                            sx={{ borderBottom: '1px solid #dee2e6' }}
                          />
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                )}

                {activeTab === 2 && (
                  <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4 }}>
                    {/* Left Column */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      {/* GENERAL Section */}
                      <Typography sx={{ fontWeight: 600, color: '#333', fontSize: '13px', mb: 2, pb: 1, borderBottom: '1px solid #e0e0e0' }}>
                        GENERAL
                      </Typography>
                      <Box sx={{ mb: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography sx={{ width: 140, color: '#495057', fontSize: '13px' }}>Banks</Typography>
                          <TextField
                            name="banks"
                            value={formData.banks}
                            onChange={handleChange}
                            placeholder="Add bank account..."
                            variant="standard"
                            size="small"
                            fullWidth
                            InputProps={{ disableUnderline: true, sx: { fontSize: '13px' } }}
                            sx={{ borderBottom: '1px solid #dee2e6' }}
                          />
                        </Box>
                      </Box>

                      {/* CUSTOMER INVOICES Section */}
                      <Typography sx={{ fontWeight: 600, color: '#333', fontSize: '13px', mb: 2, pb: 1, borderBottom: '1px solid #e0e0e0' }}>
                        CUSTOMER INVOICES
                      </Typography>
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                          <Typography sx={{ width: 140, color: '#495057', fontSize: '13px' }}>Invoice sending</Typography>
                          <Typography sx={{ fontSize: '13px', color: '#017e84' }}>e-Invoicing & Email</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                          <Typography sx={{ width: 140, color: '#495057', fontSize: '13px' }}>eInvoice format</Typography>
                          <Typography sx={{ fontSize: '13px', color: '#017e84' }}>XML format</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography sx={{ width: 140, color: '#495057', fontSize: '13px' }}>Peppol ID</Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <TextField
                              select
                              value=""
                              variant="standard"
                              size="small"
                              sx={{ width: 50, '& .MuiSelect-select': { fontSize: '13px' } }}
                              InputProps={{ disableUnderline: true }}
                            >
                              <MenuItem value="">▼</MenuItem>
                            </TextField>
                            <Typography sx={{ fontSize: '13px', color: '#017e84' }}>Your endpoint</Typography>
                          </Box>
                        </Box>
                      </Box>
                    </Box>

                    {/* Right Column */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      {/* INVOICE FOLLOW-UPS Section */}
                      <Typography sx={{ fontWeight: 600, color: '#333', fontSize: '13px', mb: 2, pb: 1, borderBottom: '1px solid #e0e0e0' }}>
                        INVOICE FOLLOW-UPS
                      </Typography>
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                          <Typography sx={{ width: 140, color: '#495057', fontSize: '13px' }}>Follow-up Status</Typography>
                          <Chip 
                            label="No action needed" 
                            size="small" 
                            sx={{ 
                              backgroundColor: '#6c757d', 
                              color: 'white', 
                              fontSize: '11px',
                              height: 22,
                              borderRadius: '12px'
                            }} 
                          />
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                          <Typography sx={{ width: 140, color: '#495057', fontSize: '13px' }}>Reminders</Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <RadioGroup
                              row
                              value={formData.reminders}
                              onChange={(e) => setFormData({ ...formData, reminders: e.target.value })}
                            >
                              <FormControlLabel
                                value="automatic"
                                control={<Radio size="small" sx={{ color: '#00A09D', '&.Mui-checked': { color: '#00A09D' } }} />}
                                label={<Typography sx={{ fontSize: '13px' }}>Automatic</Typography>}
                              />
                              <FormControlLabel
                                value="manual"
                                control={<Radio size="small" sx={{ color: '#6c757d' }} />}
                                label={<Typography sx={{ fontSize: '13px' }}>Manual</Typography>}
                              />
                            </RadioGroup>
                            <Typography sx={{ fontSize: '13px', color: '#017e84', cursor: 'pointer' }}>Send</Typography>
                          </Box>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                          <Typography sx={{ width: 140, color: '#495057', fontSize: '13px' }}>
                            Next reminder <sup style={{ color: '#875A7B', cursor: 'help' }}>?</sup>
                          </Typography>
                          <TextField
                            name="nextReminder"
                            value={formData.nextReminder}
                            onChange={handleChange}
                            placeholder="Select date..."
                            variant="standard"
                            size="small"
                            fullWidth
                            InputProps={{ disableUnderline: true, sx: { fontSize: '13px' } }}
                            sx={{ borderBottom: '1px solid #dee2e6' }}
                          />
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography sx={{ width: 140, color: '#495057', fontSize: '13px' }}>
                            Responsible <sup style={{ color: '#875A7B', cursor: 'help' }}>?</sup>
                          </Typography>
                          <TextField
                            name="responsible"
                            value={formData.responsible}
                            onChange={handleChange}
                            placeholder="Select..."
                            variant="standard"
                            size="small"
                            fullWidth
                            InputProps={{ disableUnderline: true, sx: { fontSize: '13px' } }}
                            sx={{ borderBottom: '1px solid #dee2e6' }}
                          />
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                )}

                {activeTab === 3 && (
                  <Box>
                    <Typography sx={{ fontSize: '14px', fontWeight: 600, mb: 2, textTransform: 'uppercase', color: '#666' }}>
                      Internal Notes
                    </Typography>
                    <TextField
                      fullWidth
                      multiline
                      rows={4}
                      name="internalNotes"
                      value={formData.internalNotes}
                      onChange={handleChange}
                      placeholder="Internal notes about this contact..."
                      variant="outlined"
                      size="small"
                    />
                  </Box>
                )}
              </Box>

              {/* Save Button */}
              <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={saving || !formData.name}
                  sx={{
                    backgroundColor: '#875A7B',
                    textTransform: 'none',
                    px: 3,
                    '&:hover': { backgroundColor: '#714B68' },
                    '&:disabled': { backgroundColor: '#d0d0d0', color: '#888' },
                  }}
                >
                  {saving ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Create Contact'}
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/customers')}
                  sx={{
                    borderColor: '#ccc',
                    color: '#666',
                    textTransform: 'none',
                    '&:hover': { borderColor: '#999' },
                  }}
                >
                  Discard
                </Button>
              </Box>
            </Box>
          </Paper>
        </Box>

        {/* Right - Activity Panel */}
        <Box sx={{ width: { xs: '100%', lg: 380 }, flexShrink: 0 }}>
          <ActivityPanel
            activities={[{
              _id: 'default',
              type: 'note',
              user: { name: user?.name || 'Admin User' },
              content: 'Creating a new record...',
              createdAt: new Date(),
            }]}
            onSendMessage={(data) => toast.info('Message sent')}
            onLogNote={(data) => toast.info('Note logged')}
            onScheduleActivity={(data) => toast.info('Activity scheduled')}
            showFollowers
            followers={[]}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default CustomerForm;
